import { useQueryClient } from '@tanstack/react-query';
import {
  DRAWER_OPEN_REASONS,
  lineTotalCents,
  type OrderDetailDTO,
  type PosProductDTO,
  type PosStateDTO,
  type SaleDTO,
  type SaleInput,
} from '@bifeai/shared';
import { Badge, Button, Callout, Card, CardBody, Dialog, EmptyState, Field, Input, Select, Skeleton, useToast } from '@bifeai/ui';
import {
  Archive,
  Barcode,
  Clock,
  Minus,
  Package,
  Percent,
  Plus,
  Printer,
  Receipt,
  Scale,
  ScanLine,
  Trash2,
  UserRoundCog,
  Wallet,
  X,
  Container,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { CATEGORY_ICON_COMPONENTS, ProductThumb } from '../../components/inventory';
import { ClockDialog } from '../../components/ClockDialog';
import { PinSwitchDialog } from '../../components/PinSwitchDialog';
import { api, errorMessage, isApiError } from '../../lib/api';
import { formatMoney } from '../../lib/format';
import { makeLine, newClientId, scanLocally, usePosCatalog, usePosState, type CartLine } from '../../lib/pos';
import { enqueueSale, isNetworkError, offlineAllowed, readSnapshot, saveSnapshot, useOffline } from '../../lib/offline';
import { useSession } from '../../lib/session';
import { useHardware } from '../../lib/hardware';
import { connectScale, scaleFresh, useScale } from '../../lib/scale-link';
import { printDrawerSlip, printSale, saleInvoice } from '../../lib/ticket-print';
import { FiscalLine } from '../../components/FiscalLine';
import { centsToPesosInput, formatQty, fromInternalQty, pesosToCents, priceUnit, toInternalQty } from '../../lib/units';
import { PaymentDialog, type ChargeExtra, type ChargeResult } from './PaymentDialog';
import { WholesaleCollectDialog } from './WholesaleCollectDialog';

/** Pitido corto para avisar un error de lectura (el cajero no siempre mira la pantalla). */
function beep(ok: boolean) {
  try {
    const ctx = new AudioContext();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.frequency.value = ok ? 1200 : 220;
    g.gain.value = 0.08;
    o.connect(g).connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + (ok ? 0.06 : 0.25));
    o.onended = () => void ctx.close();
  } catch {
    /* sin audio */
  }
}

export function PosPage() {
  const state = usePosState();
  if (state.isPending) {
    return (
      <div style={{ display: 'grid', gap: 12 }}>
        <Skeleton style={{ height: 60 }} />
        <Skeleton style={{ height: 400 }} />
      </div>
    );
  }
  if (state.isError) {
    return <Callout tone="danger">{errorMessage(state.error)}</Callout>;
  }
  return state.data.session ? <Counter state={state.data} /> : <OpenCash state={state.data} />;
}

// ---------------------------------------------------------------- abrir caja
function OpenCash({ state }: { state: PosStateDTO }) {
  const { me, can, isHeadquartersUser, selectedBranchId } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const [opening, setOpening] = useState('');
  const [branchId, setBranchId] = useState(state.branchId ?? selectedBranchId ?? me.branches.find((b) => b.isHeadquarters)?.id ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  const [clockOpen, setClockOpen] = useState(false);
  const chooseBranch = !state.device && isHeadquartersUser;

  async function submit(e: FormEvent) {
    e.preventDefault();
    const cents = opening.trim() ? pesosToCents(opening) : 0;
    if (cents === null) return setError('Ingresá un importe válido.');
    setBusy(true);
    setError(null);
    try {
      await api.post('/cash/open', {
        openingCents: cents,
        branchId: chooseBranch ? branchId : null,
      });
      toast.success('Caja abierta', '¡Buenas ventas!');
      await qc.invalidateQueries({ queryKey: ['pos'] });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bf-pos-open">
      <Card>
        <CardBody>
          <div className="bf-pos-open-head">
            <span className="bf-empty-icon">
              <Wallet />
            </span>
            <div>
              <h2 className="bf-card-title">Abrir la caja</h2>
              <p className="bf-card-subtitle">
                {state.device ? `Terminal ${state.device.name}` : 'Caja personal (esta computadora no es una terminal registrada)'}
                {state.branchName ? ` · ${state.branchName}` : ''} · {me.user.name}
              </p>
            </div>
          </div>
          {!can('cash.own') ? (
            <Callout tone="warning">No tenés permiso para abrir la caja. Pedile a un encargado que la abra.</Callout>
          ) : (
            <form className="bf-form" onSubmit={submit} noValidate style={{ marginTop: 16 }}>
              {error && <Callout tone="danger">{error}</Callout>}
              <Callout>
                Contá el efectivo con el que arrancás (el <strong>cambio</strong>) y escribilo acá. Al cerrar, el sistema te dice cuánto tendría que
                haber en el cajón.
              </Callout>
              {chooseBranch && (
                <Field label="Sucursal donde vendés">
                  <Select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                    {me.branches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}
              <Field label="Efectivo inicial (cambio)">
                <Input inputMode="decimal" value={opening} onChange={(e) => setOpening(e.target.value)} placeholder="$ 0" autoFocus />
              </Field>
              <div className="bf-form-actions">
                {me.tenant.modules.includes('ATTENDANCE') && (
                  <Button icon={<Clock />} onClick={() => setClockOpen(true)} style={{ marginRight: state.device ? undefined : 'auto' }}>
                    Entrada y salida
                  </Button>
                )}
                {state.device && (
                  <Button icon={<UserRoundCog />} onClick={() => setPinOpen(true)} style={{ marginRight: 'auto' }}>
                    Cambiar de cajero
                  </Button>
                )}
                <Button type="submit" variant="primary" loading={busy}>
                  Abrir caja
                </Button>
              </div>
            </form>
          )}
        </CardBody>
      </Card>
      <PinSwitchDialog open={pinOpen} onClose={() => setPinOpen(false)} onSwitched={() => setPinOpen(false)} />
      <ClockDialog open={clockOpen} onClose={() => setClockOpen(false)} />
    </div>
  );
}

// ---------------------------------------------------------------- mostrador
type Banner = { tone: 'danger' | 'info'; text: string } | null;

function Counter({ state }: { state: PosStateDTO }) {
  const { me } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const catalog = usePosCatalog(true);
  const products = useMemo(() => catalog.data ?? [], [catalog.data]);
  const settings = state.settings;
  const net = useOffline(me.user.id);

  const [lines, setLines] = useState<CartLine[]>([]);
  const [discountCents, setDiscountCents] = useState(0);
  const clientId = useRef(newClientId());
  const [banner, setBanner] = useState<Banner>(null);
  const [lastSale, setLastSale] = useState<SaleDTO | null>(null);
  const [badLines, setBadLines] = useState<Set<string>>(new Set());
  const [category, setCategory] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [weighing, setWeighing] = useState<{
    product: PosProductDTO;
    line?: CartLine;
    /** Etiqueta de balanza de un producto con precio al momento: el peso ya viene. */
    labelQty?: number;
    barcode?: string;
  } | null>(null);
  const [dupScan, setDupScan] = useState<CartLine | null>(null);
  const [paying, setPaying] = useState(false);
  const [discountOpen, setDiscountOpen] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  const [clockOpen, setClockOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [wsOpen, setWsOpen] = useState(false);
  const hw = useHardware();
  // Balanza conectada a esta computadora: se reconecta sola al entrar.
  useEffect(() => {
    if (hw.scale.enabled) void connectScale();
  }, [hw.scale.enabled]);
  const scanRef = useRef<HTMLInputElement>(null);
  const [scanValue, setScanValue] = useState('');
  const dialogOpen = Boolean(weighing || dupScan || paying || discountOpen || pinOpen || clockOpen || drawerOpen || wsOpen);

  // Pedido de "Pedidos y chat" que se cobra en esta caja (llega con ?pedido=…): carga sus productos con lo que pesaron.
  const [params, setParams] = useSearchParams();
  const pedido = params.get('pedido');
  const orderRef = useRef<{ id: string; number: number | null } | null>(null);
  useEffect(() => {
    if (!pedido || products.length === 0) return;
    setParams({}, { replace: true });
    api
      .get<OrderDetailDTO>(`/orders-chat/orders/${pedido}`)
      .then((o) => {
        const loaded = o.items.flatMap((i) => {
          const p = products.find((x) => x.id === i.productId);
          return p ? [makeLine(p, i.qtyActual ?? i.qtyRequested)] : [];
        });
        if (loaded.length < o.items.length) toast.error('Faltan productos del pedido', 'Alguno ya no está a la venta: agregalo a mano.');
        setLines(loaded);
        orderRef.current = { id: o.id, number: o.number };
        toast.info(`Pedido #${o.number} cargado`, 'Revisá los pesos y cobralo como siempre.');
      })
      .catch((err: unknown) => toast.error('No se pudo cargar el pedido', errorMessage(err)));
  }, [pedido, products, setParams, toast]);

  const subtotal = lines.reduce((a, l) => a + l.lineTotalCents, 0);
  const total = Math.max(0, subtotal - discountCents);

  const focusScan = useCallback(() => {
    if (!dialogOpen) scanRef.current?.focus({ preventScroll: true });
  }, [dialogOpen]);
  useEffect(() => {
    focusScan();
  }, [focusScan, lines.length]);

  const addLine = useCallback((line: CartLine) => {
    setLastSale(null);
    setBanner(null);
    setLines((ls) => {
      // Productos por unidad (tocados en la grilla o escaneados por código de barras): suma al mismo renglón.
      if (line.product.unitType === 'UNIT' && !line.product.openPrice) {
        const idx = ls.findIndex((l) => l.product.id === line.product.id && l.barcode === line.barcode);
        if (idx >= 0) {
          const prev = ls[idx]!;
          const next = [...ls];
          next[idx] = {
            ...makeLine(prev.product, prev.qty + line.qty, prev.barcode),
            key: prev.key,
          };
          return next;
        }
      }
      return [...ls, line];
    });
    beep(true);
  }, []);

  const handleScan = useCallback(
    (raw: string) => {
      const code = raw.trim();
      if (!code) return;
      if (!/^\d+$/.test(code)) {
        setSearch(code);
        return;
      }
      const r = scanLocally(code, products, settings.scale);
      if (!r.ok) {
        setBanner({ tone: 'danger', text: r.message });
        beep(false);
        return;
      }
      if (r.line.barcode && lines.some((l) => l.barcode === r.line.barcode) && settings.scale.enabled && r.line.product.unitType === 'WEIGHT') {
        setDupScan(r.line);
        return;
      }
      if (r.needsPrice) {
        setWeighing({ product: r.line.product, labelQty: r.line.qty, barcode: r.line.barcode ?? undefined });
        return;
      }
      addLine(r.line);
    },
    [products, settings.scale, lines, addLine],
  );

  // Lector conectado como teclado: si el foco no está en un campo, igual se capturan los números + Enter.
  useEffect(() => {
    let buffer = '';
    let last = 0;
    const onKey = (e: KeyboardEvent) => {
      if (dialogOpen) return;
      const el = document.activeElement as HTMLElement | null;
      const typing = el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable);
      if (e.key === 'F2') {
        e.preventDefault();
        if (lines.length) setPaying(true);
        return;
      }
      if (typing) return;
      const now = Date.now();
      if (now - last > 80) buffer = '';
      last = now;
      if (/^\d$/.test(e.key)) buffer += e.key;
      else if (e.key === 'Enter' && buffer.length >= 4) {
        handleScan(buffer);
        buffer = '';
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dialogOpen, handleScan, lines.length]);

  function onTile(p: PosProductDTO) {
    if (p.openPrice) {
      setWeighing({ product: p });
      return;
    }
    if (!p.priceCents) {
      setBanner({ tone: 'danger', text: `${p.name} no tiene precio cargado.` });
      beep(false);
      return;
    }
    if (p.unitType === 'WEIGHT') setWeighing({ product: p });
    else addLine(makeLine(p, 1));
  }

  function changeQty(line: CartLine, delta: number) {
    setLines((ls) =>
      ls.flatMap((l) => {
        if (l.key !== line.key) return [l];
        const qty = l.qty + delta;
        return qty <= 0 ? [] : [{ ...makeLine(l.product, qty, l.barcode), key: l.key }];
      }),
    );
  }

  function removeLine(line: CartLine) {
    setLines((ls) => ls.filter((l) => l.key !== line.key));
    setBadLines((s) => {
      const n = new Set(s);
      n.delete(line.key);
      return n;
    });
  }

  function clearTicket() {
    orderRef.current = null;
    setLines([]);
    setDiscountCents(0);
    setBadLines(new Set());
    setBanner(null);
    clientId.current = newClientId();
  }

  async function charge(payments: SaleInput['payments'], cashTenderedCents: number | null, extra: ChargeExtra): Promise<ChargeResult> {
    const body: SaleInput = {
      ...extra,
      clientId: clientId.current,
      items: lines.map((l) => ({
        ...(l.barcode ? { barcode: l.barcode, qty: l.product.unitType === 'UNIT' ? l.qty : undefined } : { productId: l.product.id, qty: l.qty }),
        // Precio al momento: se manda el precio acordado.
        ...(l.product.openPrice ? { unitPriceCents: l.unitPriceCents } : {}),
        ...(l.fromScale && !l.barcode ? { fromScale: true } : {}),
      })),
      discountCents,
      payments,
      cashTenderedCents,
    };
    try {
      const sale = await api.post<SaleDTO>('/sales', body);
      saveSnapshot('print', sale.print);
      setPaying(false);
      setLastSale(sale);
      setLines([]);
      setDiscountCents(0);
      setBadLines(new Set());
      clientId.current = newClientId();
      if (settings.ticket.autoPrint) printSale(sale);
      if (sale.customer) void qc.invalidateQueries({ queryKey: ['customers'] });
      void qc.invalidateQueries({ queryKey: ['pos', 'state'] });
      if (orderRef.current) {
        const order = orderRef.current;
        orderRef.current = null;
        api
          .post(`/orders-chat/orders/${order.id}/charged`, { saleId: sale.id })
          .then(() => qc.invalidateQueries({ queryKey: ['orders-chat'] }))
          .catch(() => toast.error(`El pedido #${order.number} no quedó como entregado`, 'Marcalo a mano en Pedidos.'));
      }
      return null;
    } catch (err) {
      // Sin internet: se guarda en esta computadora y se sube sola cuando vuelva la conexión.
      if (isNetworkError(err)) {
        if (!payments.every((p) => offlineAllowed(p.method)) || extra.customerId || extra.invoice) {
          return { message: 'Sin internet solo se puede cobrar en efectivo, con tarjeta en el posnet o por transferencia.' };
        }
        const offlineBody: SaleInput = {
          ...body,
          customerId: null,
          invoice: null,
          offlineAt: new Date().toISOString(),
          // Se guarda el precio y el importe de cada renglón tal como se le cobró al cliente.
          items: body.items.map((it, i) => ({ ...it, unitPriceCents: lines[i]!.unitPriceCents, lineTotalCents: lines[i]!.lineTotalCents })),
        };
        enqueueSale(offlineBody, { id: me.user.id, name: me.user.name });
        const provisional = provisionalSale(offlineBody, lines, state, me.tenant.name, me.user.name, cashTenderedCents);
        setPaying(false);
        setLastSale(provisional);
        setLines([]);
        setDiscountCents(0);
        setBadLines(new Set());
        clientId.current = newClientId();
        if (settings.ticket.autoPrint) printSale(provisional);
        toast.info('Venta guardada sin internet', 'Se sube sola cuando vuelva la conexión.');
        return null;
      }
      if (isApiError(err) && err.fields) {
        const bad = new Set<string>();
        for (const k of Object.keys(err.fields)) {
          const m = /^items\.(\d+)/.exec(k);
          const line = m ? lines[Number(m[1])] : undefined;
          if (line) bad.add(line.key);
        }
        setBadLines(bad);
      }
      if (isApiError(err, 'CASH_CLOSED')) void qc.invalidateQueries({ queryKey: ['pos'] });
      return {
        message: errorMessage(err),
        code: isApiError(err) ? err.code : undefined,
      };
    }
  }

  const categories = useMemo(() => {
    const map = new Map<string, { id: string; name: string; icon: string | null }>();
    for (const p of products)
      if (p.categoryId && p.categoryName)
        map.set(p.categoryId, {
          id: p.categoryId,
          name: p.categoryName,
          icon: p.categoryIcon,
        });
    return [...map.values()];
  }, [products]);
  const q = search.trim().toLowerCase();
  const visible = products.filter(
    (p) =>
      (category === 'all' || p.categoryId === category) &&
      (!q || p.name.toLowerCase().includes(q) || (p.plu ?? '').includes(q) || (p.sku ?? '').toLowerCase().includes(q)),
  );

  return (
    <div className="bf-pos">
      <section className="bf-pos-catalog" aria-label="Productos">
        <form
          className="bf-pos-scan"
          onSubmit={(e) => {
            e.preventDefault();
            handleScan(scanValue);
            setScanValue('');
          }}
        >
          <ScanLine aria-hidden />
          <input
            ref={scanRef}
            className="bf-input"
            value={scanValue}
            onChange={(e) => {
              setScanValue(e.target.value);
              if (!/^\d*$/.test(e.target.value)) setSearch(e.target.value);
              else if (search) setSearch('');
            }}
            placeholder="Escaneá la etiqueta o buscá un producto"
            aria-label="Escanear o buscar"
            autoComplete="off"
          />
          {scanValue && (
            <button
              type="button"
              className="bf-icon-btn"
              aria-label="Limpiar"
              onClick={() => {
                setScanValue('');
                setSearch('');
                focusScan();
              }}
            >
              <X />
            </button>
          )}
        </form>

        <div
          className="bf-chips bf-pos-cats"
          role="group"
          aria-label="Categorías"
          onWheel={(e) => {
            // La ruedita del mouse mueve la fila de categorías de costado.
            if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) e.currentTarget.scrollLeft += e.deltaY;
          }}
        >
          <button type="button" className="bf-chip" aria-pressed={category === 'all'} onClick={() => setCategory('all')}>
            Todo
          </button>
          {categories.map((c) => {
            const Icon = CATEGORY_ICON_COMPONENTS[(c.icon ?? 'generic') as keyof typeof CATEGORY_ICON_COMPONENTS] ?? Package;
            return (
              <button key={c.id} type="button" className="bf-chip" aria-pressed={category === c.id} onClick={() => setCategory(c.id)}>
                <Icon size={15} aria-hidden /> {c.name}
              </button>
            );
          })}
        </div>

        {catalog.isPending ? (
          <div className="bf-pos-grid">
            {Array.from({ length: 8 }, (_, i) => (
              <Skeleton key={i} style={{ height: 150, borderRadius: 12 }} />
            ))}
          </div>
        ) : products.length === 0 ? (
          <EmptyState icon={<Package />} title="No hay productos para vender" text={<Link to="/productos">Cargá el catálogo en Productos.</Link>} />
        ) : (
          <div className="bf-pos-grid">
            {visible.map((p) => (
              <button key={p.id} type="button" className="bf-pos-tile" onClick={() => onTile(p)} data-noprice={!p.priceCents && !p.openPrice}>
                <ProductThumb photoUrl={p.photoUrl} icon={p.categoryIcon as never} name={p.name} size="lg" />
                <span className="bf-pos-tile-name">{p.name}</span>
                <span className="bf-pos-tile-price">
                  {p.openPrice ? (
                    'Precio al momento'
                  ) : p.priceCents ? (
                    <>
                      {formatMoney(p.priceCents)}
                      <small>{priceUnit(p.unitType)}</small>
                    </>
                  ) : (
                    'Sin precio'
                  )}
                </span>
                {p.plu && <span className="bf-pos-tile-plu">{p.plu}</span>}
              </button>
            ))}
            {visible.length === 0 && <p className="bf-text-muted">Sin resultados.</p>}
          </div>
        )}
      </section>

      <aside className="bf-pos-ticket" aria-label="Ticket">
        <header className="bf-pos-ticket-head">
          <div>
            <strong>{state.branchName}</strong>
            <div className="bf-cell-sub">
              {state.device ? state.device.name : 'Caja personal'} · {state.cashier.name}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            {hw.scale.enabled && <ScaleChip />}
            {hw.drawer && (
              <Button size="sm" variant="ghost" iconOnly icon={<Archive />} title="Abrir cajón sin venta" onClick={() => setDrawerOpen(true)}>
                Abrir cajón
              </Button>
            )}
            {me.tenant.modules.includes('WHOLESALE') && (
              <Button size="sm" variant="ghost" iconOnly icon={<Container />} title="Cobrar pedido mayorista" onClick={() => setWsOpen(true)}>
                Cobrar pedido mayorista
              </Button>
            )}
            {me.tenant.modules.includes('ATTENDANCE') && (
              <Button size="sm" variant="ghost" iconOnly icon={<Clock />} onClick={() => setClockOpen(true)}>
                Entrada y salida
              </Button>
            )}
            {state.device && (
              <Button size="sm" variant="ghost" iconOnly icon={<UserRoundCog />} onClick={() => setPinOpen(true)}>
                Cambiar de cajero
              </Button>
            )}
            <Link to="/caja" className="bf-btn bf-btn--ghost bf-btn--sm bf-btn--icon" title="Caja" aria-label="Caja">
              <Wallet />
            </Link>
          </div>
        </header>

        {banner && (
          <div className={`bf-pos-banner bf-pos-banner--${banner.tone}`} role="alert">
            {banner.text}
            <button type="button" className="bf-icon-btn" aria-label="Cerrar aviso" onClick={() => setBanner(null)}>
              <X />
            </button>
          </div>
        )}

        <div className="bf-pos-lines">
          {lines.length === 0 && !lastSale && (
            <div className="bf-pos-empty">
              <Barcode aria-hidden />
              <p>Escaneá la etiqueta de la balanza o tocá un producto.</p>
            </div>
          )}
          {lines.length === 0 && lastSale && (
            <div className="bf-pos-done">
              <Badge tone="success">Venta cobrada · {lastSale.ticket}</Badge>
              <div className="bf-pos-done-total">{formatMoney(lastSale.totalCents)}</div>
              {lastSale.changeCents > 0 && (
                <div className="bf-pos-change">
                  Vuelto <strong>{formatMoney(lastSale.changeCents)}</strong>
                </div>
              )}
              {lastSale.customer && (
                <div className="bf-cell-sub">
                  {lastSale.customer.name}
                  {lastSale.customer.balanceAfterCents !== null && <> · saldo en cuenta {formatMoney(lastSale.customer.balanceAfterCents)}</>}
                </div>
              )}
              <FiscalLine sale={lastSale} onUpdate={setLastSale} />
              <Button icon={<Printer />} onClick={() => printSale(lastSale)}>
                {saleInvoice(lastSale) ? 'Imprimir factura' : 'Imprimir ticket'}
              </Button>
            </div>
          )}
          {lines.map((l) => (
            <div key={l.key} className="bf-pos-line" data-bad={badLines.has(l.key)}>
              <div className="bf-pos-line-main">
                <span className="bf-pos-line-name">{l.product.name}</span>
                <span className="bf-cell-sub">
                  {formatQty(l.product.unitType, l.qty)} × {formatMoney(l.unitPriceCents)}
                  {priceUnit(l.product.unitType)}
                  {l.barcode && l.product.unitType === 'WEIGHT' ? ' · etiqueta' : l.fromScale ? ' · balanza' : ''}
                </span>
              </div>
              <div className="bf-pos-line-actions">
                {l.product.unitType === 'UNIT' ? (
                  <>
                    <button type="button" className="bf-icon-btn" aria-label="Uno menos" onClick={() => changeQty(l, -1)}>
                      <Minus />
                    </button>
                    <button type="button" className="bf-icon-btn" aria-label="Uno más" onClick={() => changeQty(l, 1)}>
                      <Plus />
                    </button>
                  </>
                ) : (
                  (!l.barcode || l.product.openPrice) && (
                    <button
                      type="button"
                      className="bf-pos-line-edit"
                      onClick={() => setWeighing({ product: l.product, line: l, labelQty: l.barcode ? l.qty : undefined })}
                    >
                      Editar
                    </button>
                  )
                )}
                <span className="bf-pos-line-total">{formatMoney(l.lineTotalCents)}</span>
                <button type="button" className="bf-icon-btn" aria-label={`Quitar ${l.product.name}`} onClick={() => removeLine(l)}>
                  <Trash2 />
                </button>
              </div>
            </div>
          ))}
        </div>

        <footer className="bf-pos-totals">
          {discountCents > 0 && (
            <>
              <div className="bf-pos-row">
                <span>Subtotal</span>
                <span className="bf-num">{formatMoney(subtotal)}</span>
              </div>
              <div className="bf-pos-row bf-text-success">
                <span>Descuento</span>
                <span className="bf-num">−{formatMoney(discountCents)}</span>
              </div>
            </>
          )}
          <div className="bf-pos-row bf-pos-total">
            <span>Total</span>
            <span className="bf-num">{formatMoney(total)}</span>
          </div>
          <div className="bf-pos-actions">
            {lines.length > 0 && (
              <Button variant="ghost" icon={<Trash2 />} onClick={clearTicket}>
                Vaciar
              </Button>
            )}
            {state.cashier.canDiscount && lines.length > 0 && (
              <Button icon={<Percent />} onClick={() => setDiscountOpen(true)}>
                Descuento
              </Button>
            )}
            <Button variant="primary" className="bf-pos-pay" icon={<Receipt />} disabled={lines.length === 0} onClick={() => setPaying(true)}>
              Cobrar <kbd>F2</kbd>
            </Button>
          </div>
        </footer>
      </aside>

      {weighing && (
        <WeightDialog
          product={weighing.product}
          initialQty={weighing.line?.qty ?? weighing.labelQty}
          lockedQty={weighing.labelQty !== undefined}
          onClose={() => setWeighing(null)}
          onConfirm={(qty, priceCents, fromScale) => {
            // Precio al momento: el renglón guarda el precio acordado.
            const product = weighing.product.openPrice ? { ...weighing.product, priceCents } : weighing.product;
            if (weighing.line) {
              const key = weighing.line.key;
              setLines((ls) => ls.map((l) => (l.key === key ? { ...makeLine(product, qty, l.barcode), key, fromScale } : l)));
            } else addLine({ ...makeLine(product, qty, weighing.barcode ?? null), fromScale });
            setWeighing(null);
          }}
        />
      )}

      <Dialog
        open={Boolean(dupScan)}
        onOpenChange={(o) => !o && setDupScan(null)}
        title="¿Etiqueta repetida?"
        description="Esta misma etiqueta ya está en el ticket. Si es otra pieza con el mismo peso, agregala de nuevo."
      >
        <div className="bf-form-actions">
          <Button onClick={() => setDupScan(null)} autoFocus>
            No, era la misma
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              if (dupScan) addLine({ ...dupScan });
              setDupScan(null);
            }}
          >
            Sí, agregar
          </Button>
        </div>
      </Dialog>

      {discountOpen && (
        <DiscountDialog
          subtotal={subtotal}
          current={discountCents}
          maxPercent={state.cashier.maxDiscountPercent}
          onClose={() => setDiscountOpen(false)}
          onApply={(c) => {
            setDiscountCents(c);
            setDiscountOpen(false);
          }}
        />
      )}

      {paying && (
        <PaymentDialog
          totalCents={total}
          enabled={settings.sales.enabledPayments}
          branchId={state.session?.branchId ?? state.branchId}
          terminals={settings.sales.terminals}
          offline={!net.online}
          onClose={() => setPaying(false)}
          onCharge={charge}
        />
      )}

      <PinSwitchDialog
        open={pinOpen}
        onClose={() => setPinOpen(false)}
        onSwitched={() => {
          setPinOpen(false);
          toast.success('Cajero cambiado');
        }}
      />
      <ClockDialog open={clockOpen} onClose={() => setClockOpen(false)} />
      {wsOpen && <WholesaleCollectDialog onClose={() => setWsOpen(false)} />}
      {drawerOpen && (
        <DrawerDialog
          onClose={() => setDrawerOpen(false)}
          onOpened={(reason) => {
            setDrawerOpen(false);
            printDrawerSlip(settings.ticket.businessName || me.tenant.name, state.cashier.name, reason, settings.ticket);
            toast.success('Cajón abierto', 'Quedó registrado con el motivo.');
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------- balanza conectada
function ScaleChip() {
  const scale = useScale();
  const [, tick] = useState(0);
  // Se refresca para notar si la balanza dejó de mandar datos.
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const ok = scaleFresh(scale);
  if (scale.status === 'unsupported') return null;
  return (
    <button
      type="button"
      className={`bf-pos-scale-chip ${ok ? 'is-ok' : 'is-off'}`}
      onClick={() => !ok && void connectScale(scale.status !== 'connected')}
      title={ok ? 'Balanza conectada' : 'Balanza sin señal: tocá para conectar'}
    >
      <Scale aria-hidden size={15} />
      {ok ? formatQty('WEIGHT', Math.max(0, scale.grams ?? 0)) : 'Conectar'}
    </button>
  );
}

// ---------------------------------------------------------------- abrir cajón sin venta
function DrawerDialog({ onClose, onOpened }: { onClose: () => void; onOpened: (reason: string) => void }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (reason.trim().length < 3) return setError('Escribí o elegí el motivo.');
    setBusy(true);
    setError(null);
    try {
      await api.post('/cash/drawer', { reason: reason.trim() });
      onOpened(reason.trim());
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title="Abrir cajón sin venta"
      description="Queda registrado con tu nombre, la hora y el motivo."
    >
      <form className="bf-form" onSubmit={submit}>
        {error && <Callout tone="danger">{error}</Callout>}
        <div className="bf-chips">
          {DRAWER_OPEN_REASONS.map((r) => (
            <button key={r} type="button" className="bf-chip" aria-pressed={reason === r} onClick={() => setReason(r)}>
              {r}
            </button>
          ))}
        </div>
        <Field label="Motivo">
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ej.: cambiar billetes" maxLength={120} />
        </Field>
        <div className="bf-form-actions">
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="primary" icon={<Archive />} loading={busy}>
            Abrir cajón
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------- peso a mano (y precio al momento)
function WeightDialog({
  product,
  initialQty,
  lockedQty,
  onClose,
  onConfirm,
}: {
  product: PosProductDTO;
  initialQty?: number;
  /** El peso viene de la etiqueta: no se cambia. */
  lockedQty?: boolean;
  onClose: () => void;
  onConfirm: (qty: number, priceCents: number, fromScale: boolean) => void;
}) {
  const byWeight = product.unitType === 'WEIGHT';
  const hw = useHardware();
  const scale = useScale();
  const canScale = byWeight && !lockedQty && hw.scale.enabled && scale.status !== 'unsupported';
  const [mode, setMode] = useState<'scale' | 'kg' | 'money'>(canScale && !initialQty ? 'scale' : 'kg');
  const [, tick] = useState(0);
  useEffect(() => {
    if (mode !== 'scale') return;
    const t = setInterval(() => tick((n) => n + 1), 500);
    return () => clearInterval(t);
  }, [mode]);
  const live = scaleFresh(scale);
  const liveGrams = live ? (scale.grams ?? 0) : 0;
  const liveOk = live && scale.stable && liveGrams > 0;
  const [value, setValue] = useState(initialQty ? fromInternalQty(product.unitType, initialQty) : byWeight ? '' : '1');
  const [priceInput, setPriceInput] = useState(product.priceCents ? centsToPesosInput(product.priceCents) : '');
  const price = product.openPrice ? (pesosToCents(priceInput || '0') ?? 0) : (product.priceCents ?? 0);
  const qty =
    mode === 'scale'
      ? liveOk
        ? liveGrams
        : null
      : !byWeight || mode === 'kg'
        ? toInternalQty(product.unitType, value || '0')
        : (() => {
            const c = pesosToCents(value || '0');
            return c === null || price === 0 ? null : Math.round((c * 1000) / price);
          })();
  const valid = qty !== null && qty > 0 && price > 0;
  const per = byWeight ? 'por kilo' : 'por unidad';

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={product.name}
      description={product.openPrice ? `Precio al momento: escribí el precio ${per} que se acordó.` : `${formatMoney(price)} ${per}`}
    >
      <form
        className="bf-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) onConfirm(qty!, price, mode === 'scale');
        }}
      >
        {product.openPrice && (
          <Field label={`Precio ${per}`} hint={product.priceCents ? 'Sugerido: el último cargado en Precios.' : undefined}>
            <Input
              inputMode="decimal"
              value={priceInput}
              onChange={(e) => setPriceInput(e.target.value)}
              autoFocus
              placeholder="$ 0"
              className="bf-input--lg"
            />
          </Field>
        )}
        {lockedQty ? (
          <div className="bf-pos-preview">
            <span>Peso de la etiqueta</span>
            <strong>{formatQty(product.unitType, initialQty ?? 0)}</strong>
          </div>
        ) : (
          <>
            {byWeight && (canScale || !product.openPrice) && (
              <div className="bf-segmented bf-segmented--text" role="group" aria-label="Cargar por">
                {canScale && (
                  <button type="button" aria-pressed={mode === 'scale'} onClick={() => setMode('scale')}>
                    Balanza
                  </button>
                )}
                <button type="button" aria-pressed={mode === 'kg'} onClick={() => setMode('kg')}>
                  {canScale ? 'A mano' : 'Kilos'}
                </button>
                {!product.openPrice && (
                  <button type="button" aria-pressed={mode === 'money'} onClick={() => setMode('money')}>
                    Importe ($)
                  </button>
                )}
              </div>
            )}
            {mode === 'scale' ? (
              <div className="bf-pos-live" data-state={liveOk ? 'ok' : live ? 'moving' : 'off'} aria-live="polite">
                <span className="bf-pos-live-kg">{formatQty('WEIGHT', Math.max(0, liveGrams))}</span>
                <span className="bf-pos-live-state">
                  {liveOk ? (
                    'Peso estable'
                  ) : live ? (
                    liveGrams <= 0 ? (
                      'Poné el producto en la balanza'
                    ) : (
                      'Pesando…'
                    )
                  ) : (
                    <>
                      La balanza no manda el peso.{' '}
                      <button type="button" className="bf-link-btn" onClick={() => void connectScale(scale.status !== 'connected')}>
                        Conectar
                      </button>
                    </>
                  )}
                </span>
              </div>
            ) : (
              <>
                <Field
                  label={!byWeight ? 'Cantidad' : mode === 'kg' ? 'Peso en kilos' : 'Importe que pide el cliente'}
                  hint={!byWeight ? undefined : mode === 'kg' ? 'Ej.: 1,250 = 1 kg y cuarto' : 'Calculamos el peso a cortar.'}
                >
                  <Input
                    inputMode="decimal"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    autoFocus={!product.openPrice}
                    placeholder={byWeight ? (mode === 'kg' ? '0,000' : '$ 0') : '1'}
                    className="bf-input--lg"
                  />
                </Field>
                {byWeight && mode === 'kg' && (
                  <div className="bf-chips">
                    {['0,250', '0,500', '0,750', '1', '1,5', '2'].map((v) => (
                      <button key={v} type="button" className="bf-chip" onClick={() => setValue(v)}>
                        {v} kg
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}
        <div className="bf-pos-preview">
          {valid ? (
            <>
              <span>{formatQty(product.unitType, qty!)}</span>
              <strong>{formatMoney(lineTotalCents(product.unitType, qty!, price))}</strong>
            </>
          ) : (
            <span className="bf-text-muted">{product.openPrice ? 'Escribí el precio y la cantidad' : 'Escribí el peso o el importe'}</span>
          )}
        </div>
        <div className="bf-form-actions">
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="primary" disabled={!valid}>
            {initialQty && !lockedQty ? 'Guardar' : 'Agregar'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------- descuento
function DiscountDialog({
  subtotal,
  current,
  maxPercent,
  onClose,
  onApply,
}: {
  subtotal: number;
  current: number;
  maxPercent: number | null;
  onClose: () => void;
  onApply: (cents: number) => void;
}) {
  const [mode, setMode] = useState<'percent' | 'money'>('percent');
  const [value, setValue] = useState(current ? String(Math.round((current / subtotal) * 100)) : '');
  const n = Number(value.replace(',', '.'));
  const cents = !value ? 0 : mode === 'percent' ? Math.round((subtotal * n) / 100) : (pesosToCents(value) ?? -1);
  const pct = subtotal ? (cents / subtotal) * 100 : 0;
  const tooMuch = maxPercent !== null && pct > maxPercent + 0.001;
  const invalid = cents < 0 || cents >= subtotal || Number.isNaN(n);

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title="Descuento"
      description={maxPercent === null ? 'Sin tope (dueño).' : `Podés descontar hasta ${maxPercent}%.`}
    >
      <form
        className="bf-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (!invalid && !tooMuch) onApply(cents);
        }}
      >
        <div className="bf-segmented bf-segmented--text" role="group" aria-label="Tipo de descuento">
          <button type="button" aria-pressed={mode === 'percent'} onClick={() => setMode('percent')}>
            Porcentaje
          </button>
          <button type="button" aria-pressed={mode === 'money'} onClick={() => setMode('money')}>
            Importe ($)
          </button>
        </div>
        <Field label={mode === 'percent' ? 'Porcentaje' : 'Importe a descontar'} error={tooMuch ? `Supera el máximo de ${maxPercent}%` : undefined}>
          <Input
            inputMode="decimal"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            autoFocus
            placeholder={mode === 'percent' ? '10' : '$ 0'}
          />
        </Field>
        <div className="bf-pos-preview">
          <span>Total con descuento</span>
          <strong>{formatMoney(Math.max(0, subtotal - Math.max(0, cents)))}</strong>
        </div>
        <div className="bf-form-actions">
          {current > 0 && (
            <Button variant="ghost" onClick={() => onApply(0)} style={{ marginRight: 'auto' }}>
              Quitar descuento
            </Button>
          )}
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="primary" disabled={invalid || tooMuch}>
            Aplicar
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/**
 * Ticket provisorio de una venta cobrada sin internet. El número definitivo lo pone el sistema al
 * subirla; mientras tanto el ticket dice que está pendiente.
 */
function provisionalSale(
  body: SaleInput,
  lines: CartLine[],
  state: PosStateDTO,
  businessName: string,
  cashierName: string,
  cashTenderedCents: number | null,
): SaleDTO {
  const subtotal = lines.reduce((a, l) => a + l.lineTotalCents, 0);
  const total = subtotal - (body.discountCents ?? 0);
  const cash = body.payments.filter((p) => p.method === 'CASH').reduce((a, p) => a + p.amountCents, 0);
  const print = readSnapshot<SaleDTO['print']>('print') ?? {
    businessName: state.settings.ticket.businessName || businessName,
    cuit: null,
    branchAddress: null,
    ticket: state.settings.ticket,
  };
  return {
    id: body.clientId,
    ticket: 'PENDIENTE (sin internet)',
    offline: true,
    branchId: state.branchId ?? '',
    branchName: state.branchName ?? '',
    sessionId: state.session?.id ?? '',
    cashierName,
    status: 'COMPLETED',
    subtotalCents: subtotal,
    discountCents: body.discountCents ?? 0,
    totalCents: total,
    costCents: null,
    cashTenderedCents: cash > 0 ? cashTenderedCents : null,
    changeCents: cash > 0 && cashTenderedCents ? Math.max(0, cashTenderedCents - cash) : 0,
    notes: 'Venta sin internet: se sube al sistema cuando vuelve la conexión.',
    createdAt: body.offlineAt ?? new Date().toISOString(),
    voidedAt: null,
    voidedByName: null,
    voidReason: null,
    lines: lines.map((l, i) => ({
      id: String(i),
      productId: l.product.id,
      productName: l.product.name,
      unitType: l.product.unitType,
      qty: l.qty,
      unitPriceCents: l.unitPriceCents,
      lineTotalCents: l.lineTotalCents,
      barcode: l.barcode,
    })),
    payments: body.payments.map((p) => ({
      method: p.method,
      amountCents: p.amountCents,
      reference: p.reference ?? null,
      installments: p.method === 'CREDIT' ? (p.installments ?? 1) : null,
      terminal: p.terminal ?? null,
    })),
    customer: null,
    canVoid: false,
    print,
    fiscal: [],
    mpRefunds: [],
  };
}
